import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'antd';
import {
  deleteAdminServiceFee,
  getAdminServiceFeeList,
  updateAdminServiceFee,
} from '../../../api';
import getFormatMessage from '../../../locales/get-format-message';
import { getCurrentLangText, getLocalTime } from '../../../lib';
import { CreateModelSetting } from '../components/create-model';
import { messageError, messageSuccess } from '../../../components/message';
import { IServiceFeeModelResItem } from '../../../model/api-modules/price-calculator';
import { CommonStatusStep } from '../../../model/api-modules';
import { AutoAntTable } from '../../../components/auto-ant-table';
import {
  CommonButton,
  CommonButtonGroup,
} from '../../../components/button/main';
import { wrapRequestArea } from '../../../lib/http/lib';
import { useSelector } from 'react-redux';
import { IRootState } from '../../../store/reducers';

export const PriceModelSetting = (props: { site?: string }) => {
  const { site } = props;
  const [configList, setConfigList] = useState<IServiceFeeModelResItem[]>([]);
  const [configListLoading, setConfigListLoading] = useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [currentRecord, setCurrentRecord] =
    useState<IServiceFeeModelResItem | null>(null);
  const [showDeleteModelModal, setShowDeleteModelModal] =
    useState<boolean>(false);
  const [deleteRecord, setDeleteRecord] =
    useState<IServiceFeeModelResItem | null>(null);
  const [deleteModelLoading, setDeleteModelLoading] = useState<boolean>(false);
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);

  useEffect(() => {
    getPaymentModelList();
  }, []);

  const getPaymentModelList = async () => {
    setConfigListLoading(true);
    const res = await wrapRequestArea(getAdminServiceFeeList, site);
    if (res) {
      setConfigList(res.list);
    }
    setConfigListLoading(false);
  };

  const handleOnlineClick = async (record: IServiceFeeModelResItem) => {
    const res = await updateAdminServiceFee(record.id, {
      ModelName: record.ModelName,
      ModelRelationServices: record.ModelRelationServices,
      ComputeScriptStr: record.ComputeScriptStr,
      ScriptType: record.ScriptType,
      Status:
        record.Status === CommonStatusStep.Pending
          ? CommonStatusStep.Resolve
          : CommonStatusStep.Pending,
    });
    if (res === 0) {
      getPaymentModelList();
    }
  };

  const COLUMNS = [
    {
      title: getFormatMessage('model_name'),
      dataIndex: 'ModelName',
      key: 'ModelName',
    },
    {
      title: getFormatMessage('price_online_status'),
      dataIndex: 'Status',
      key: 'Status',
      render: (text: number) => {
        return (
          <span>
            <span
              style={{
                display: 'inline-block',
                width: '10px',
                height: '10px',
                background:
                  text === CommonStatusStep.Resolve ? '#00ff7f' : '#ccc',
                borderRadius: '5px',
                marginRight: '4px',
              }}
            ></span>
            {text === CommonStatusStep.Resolve ? 'online' : 'offline'}
          </span>
        );
      },
    },
    {
      title: getFormatMessage('create_time_local'),
      dataIndex: 'CreateTime',
      key: 'CreateTime',
      render: (text: Date) => {
        return getLocalTime(text, userInfo?.UserSettings?.Timezone);
      },
    },
    {
      title: getFormatMessage('modify_time_local'),
      dataIndex: 'ModifyTime',
      key: 'ModifyTime',
      render: (text: Date) => {
        return getLocalTime(text, userInfo?.UserSettings?.Timezone);
      },
    },
    {
      title: getFormatMessage('operate'),
      key: 'Operate',
      render: (_text: string, record: IServiceFeeModelResItem) => {
        return (
          <CommonButtonGroup>
            <CommonButton type="link" onClick={() => handleOnlineClick(record)}>
              {record.Status === 1
                ? getFormatMessage('common_disable')
                : getFormatMessage('common_enable')}
            </CommonButton>
            <CommonButton type="link" onClick={() => handleEdit(record)}>
              {getFormatMessage('edit')}
            </CommonButton>
            <CommonButton
              className="ant-btn-dangerous"
              type="link"
              onClick={() => deleteModel(record)}
            >
              {getFormatMessage('delete')}
            </CommonButton>
          </CommonButtonGroup>
        );
      },
    },
  ];

  const deleteModel = (record: IServiceFeeModelResItem) => {
    setShowDeleteModelModal(true);
    setDeleteRecord(record);
  };

  const handleEdit = (record: IServiceFeeModelResItem) => {
    setShowCreateModal(true);
    setCurrentRecord(record);
  };

  const onDeleteCancel = () => {
    setShowDeleteModelModal(false);
    setDeleteRecord(null);
  };

  const onDeleteModel = async () => {
    if (deleteRecord) {
      setDeleteModelLoading(true);
      const res = await deleteAdminServiceFee(deleteRecord?.id);
      setDeleteModelLoading(false);
      if (res === 0) {
        messageSuccess(getFormatMessage('del_success'));
        onDeleteCancel();
        getPaymentModelList();
        return;
      }
      messageError(getFormatMessage('error_delete'));
    }
  };

  const renderDeleteNewsModal = () => {
    return (
      <Modal
        open={showDeleteModelModal && !!deleteRecord}
        title={getFormatMessage('delete')}
        onCancel={onDeleteCancel}
        maskClosable={false}
        onOk={onDeleteModel}
        okText={getFormatMessage('delete')}
        confirmLoading={deleteModelLoading}
      >
        <div>{`${getFormatMessage('is_sure_delete_model')} ${getCurrentLangText(
          deleteRecord?.ModelName || ''
        )}`}</div>
      </Modal>
    );
  };

  const renderNewsModal = () => {
    if (showCreateModal) {
      return (
        <CreateModelSetting
          setShowCreateModal={setShowCreateModal}
          getPaymentModelList={getPaymentModelList}
          currentRecord={currentRecord}
          setCurrentRecord={setCurrentRecord}
        />
      );
    }
    return null;
  };

  const clickCreate = () => {
    setShowCreateModal(true);
    setCurrentRecord(null);
  };

  return (
    <div>
      <div style={{ marginBottom: '8px' }}>
        <Button
          className="gc-btn gc-btn-primary btn-admin"
          onClick={clickCreate}
        >
          {getFormatMessage('create')}
        </Button>
      </div>
      <AutoAntTable
        loading={configListLoading}
        dataSource={configList}
        columns={COLUMNS}
      />
      {renderNewsModal()}
      {renderDeleteNewsModal()}
    </div>
  );
};
